import React from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { ScanPanel } from '../../../components/scanner/ScanPanel';
import {
  cameraFailed,
  cameraReadHeld,
  cameraRetried,
  cameraStalled,
  readDropped,
} from '../../../features/operator/operatorDeviceSlice';
import { operatorPrefChanged } from '../../../features/operator/operatorSlice';
import { selectOperatorPrefs } from '../../../features/operator/operatorUtils';

/** What the panel shows of the device; a touch elsewhere on the screen does not draw the camera again. */
const selectScanDevice = (state) => {
  const { camera, sheets, signal, dropped } = state.operatorDevice;
  return { camera, sheets, signal, dropped };
};

/**
 * The scan panel, wired to this device: the camera preference, its failures,
 * the pause while a sheet covers it, the read that comes in while the screen
 * is busy, which the store holds, the camera read it rejected last, and a
 * read that was not read. The screen says what to scan and what a read means.
 *
 * @param {object} props
 * @param {string} props.prompt
 * @param {string} [props.hint]
 * @param {boolean} props.accepting
 * @param {string|null} [props.lastCode] - The flow's last accepted code, as its scan gate knows it.
 * @param {boolean} [props.holdReads=true] - Off where a read leaves the screen, as a queue's does.
 * @param {string|null} [props.rereadOn] - The screen's step, where one camera serves several: moving on offers
 *   again what the camera sees.
 * @param {string} [props.busyLabel]
 * @param {(code: string, source: string, alternatives?: string[], read?: {again: boolean}) => void} props.onCode
 *   `read.again`: the camera saw the code come back into view.
 * @param {string} [props.repeatLabel]
 * @param {() => void} [props.onRepeat]
 */
export const OperatorScanPanel = ({ holdReads = true, ...props }) => {
  const dispatch = useDispatch();
  const prefs = useSelector(selectOperatorPrefs);
  const { camera, sheets, signal, dropped } = useSelector(selectScanDevice, shallowEqual);

  return (
    <ScanPanel
      {...props}
      cameraOn={prefs.camera}
      cameraPaused={sheets > 0}
      cameraError={camera.error}
      cameraEpoch={camera.epoch}
      turnedAway={camera.turnedAway}
      tone={signal?.tone}
      verdictId={signal?.id ?? null}
      dropped={dropped}
      onDrop={(code) => dispatch(readDropped({ code }))}
      onHold={
        holdReads
          ? (code, alternatives, read) =>
              dispatch(cameraReadHeld({ code, alternatives, again: Boolean(read?.again), source: read?.source }))
          : undefined
      }
      onCameraToggle={(on) => dispatch(operatorPrefChanged({ pref: 'camera', value: on }))}
      onCameraError={(error) => dispatch(cameraFailed(error))}
      onCameraStall={(reason) => dispatch(cameraStalled({ reason }))}
      onCameraRetry={() => dispatch(cameraRetried())}
      cameraDiagnostics={prefs.cameraDiagnostics}
    />
  );
};
